import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check, ExternalLink, Loader2, XCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../services/supabase'
import { useEfiCartao } from '../hooks/useEfiCartao'
import { mensagemRecusaCartao } from '../utils/cartaoEfiStatus'
import Button from '../components/Button'

const POLL_MS = 5000
const POLL_MAX_MS = 5 * 60 * 1000
const STORAGE_KEY = 'efi_cartao_link_pendente'

interface PendenteLink {
  chargeId: number | null
  subscriptionId: number | null
  planoId: string
  assinaturaId?: string | null
  paymentUrl?: string | null
  planoNome?: string | null
}

function lerPendente(): PendenteLink | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as PendenteLink
  } catch {
    return null
  }
}

export default function CartaoLinkAguardando() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, loading: authLoading } = useAuth()
  const { consultarStatusCartao, confirmarPagamentoCartao } = useEfiCartao()

  const planoId = searchParams.get('plano') || ''
  const [pendente, setPendente] = useState<PendenteLink | null>(null)
  const [etapa, setEtapa] = useState<'aguardando' | 'sucesso' | 'recusado' | 'timeout'>(
    'aguardando'
  )
  const [mensagemErro, setMensagemErro] = useState<string | null>(null)
  const [pollKey, setPollKey] = useState(0)
  const pollDesdeRef = useRef(Date.now())

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login')
    }
  }, [authLoading, user, navigate])

  useEffect(() => {
    const stored = lerPendente()
    if (stored && (!planoId || stored.planoId === planoId)) {
      setPendente(stored)
      return
    }
    const chargeId = Number(searchParams.get('chargeId') || 0) || null
    const subscriptionId = Number(searchParams.get('subscriptionId') || 0) || null
    if (planoId && (chargeId || subscriptionId)) {
      setPendente({
        chargeId,
        subscriptionId,
        planoId,
        paymentUrl: searchParams.get('paymentUrl'),
      })
    }
  }, [planoId, searchParams])

  const verificarAssinaturaAtiva = useCallback(async () => {
    if (!user || !pendente?.planoId) return false
    const { data } = await supabase
      .from('assinaturas')
      .select('id, status')
      .eq('user_id', user.id)
      .eq('plano_id', pendente.planoId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    return data?.status === 'ativa'
  }, [user, pendente?.planoId])

  const finalizarSucesso = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY)
    setEtapa('sucesso')
    window.setTimeout(() => {
      navigate('/dashboard?pagamento=sucesso&provider=efi&metodo=cartao_link')
    }, 2500)
  }, [navigate])

  useEffect(() => {
    if (!pendente || !user || etapa !== 'aguardando') return

    let cancelled = false

    const tick = async () => {
      if (cancelled) return

      if (await verificarAssinaturaAtiva()) {
        finalizarSucesso()
        return
      }

      // Consulta EFI e, se pago, ativa a assinatura no Latera (webhook + polling)
      const confirmacao = await confirmarPagamentoCartao({
        chargeId: pendente.chargeId,
        subscriptionId: pendente.subscriptionId,
        planoId: pendente.planoId,
        assinaturaId: pendente.assinaturaId,
      })

      if (cancelled) return

      if (confirmacao?.ativado || confirmacao?.confirmado) {
        if (await verificarAssinaturaAtiva() || confirmacao.ativado) {
          finalizarSucesso()
          return
        }
      }

      if (confirmacao?.classificacao === 'recusado') {
        setMensagemErro(
          mensagemRecusaCartao(confirmacao.refuseReason, confirmacao.status)
        )
        setEtapa('recusado')
        return
      }

      // Fallback: só status, sem ativação (ex.: ainda waiting)
      const resultado = confirmacao || (await consultarStatusCartao({
        chargeId: pendente.chargeId,
        subscriptionId: pendente.subscriptionId,
      }))

      if (cancelled) return

      if (resultado?.classificacao === 'recusado') {
        setMensagemErro(
          mensagemRecusaCartao(
            'refuseReason' in resultado ? resultado.refuseReason : null,
            resultado.status
          )
        )
        setEtapa('recusado')
        return
      }

      if (Date.now() - pollDesdeRef.current >= POLL_MAX_MS) {
        setEtapa('timeout')
        return
      }

      timer = window.setTimeout(tick, POLL_MS)
    }

    let timer = window.setTimeout(tick, POLL_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [
    pendente,
    user,
    etapa,
    pollKey,
    consultarStatusCartao,
    confirmarPagamentoCartao,
    verificarAssinaturaAtiva,
    finalizarSucesso,
  ])

  const abrirPagamentoEfi = () => {
    const url = pendente?.paymentUrl
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  const cancelarProcedimento = () => {
    sessionStorage.removeItem(STORAGE_KEY)
    navigate('/assinar')
  }

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    )
  }

  if (!pendente) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center space-y-4">
          <p className="text-gray-700">Nenhum pagamento pendente encontrado.</p>
          <Button variant="btn_add" onClick={() => navigate('/assinar')}>
            Escolher plano
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg w-full text-center space-y-5">
        {etapa === 'aguardando' && (
          <>
            <Loader2 className="w-14 h-14 text-blue-600 animate-spin mx-auto" />
            <h1 className="text-xl font-bold text-gray-900">Aguarde a confirmação da assinatura</h1>
            <p className="text-sm text-gray-600">
              Conclua o pagamento na aba da EFI (cartão). Esta página atualiza automaticamente quando
              o banco confirmar.
            </p>
            {pendente.paymentUrl && (
              <button
                type="button"
                onClick={abrirPagamentoEfi}
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Abrir pagamento na EFI
              </button>
            )}
            <div className="pt-2">
              <Button variant="btn_secondary" onClick={cancelarProcedimento}>
                Cancelar procedimento
              </Button>
            </div>
          </>
        )}

        {etapa === 'sucesso' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Assinatura confirmada!</h1>
            <p className="text-sm text-gray-600">Redirecionando para o painel…</p>
          </>
        )}

        {etapa === 'recusado' && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto" />
            <h1 className="text-xl font-bold text-gray-900">Pagamento não confirmado</h1>
            <p className="text-sm text-red-700">{mensagemErro}</p>
            <div className="flex flex-col gap-2 pt-2">
              {pendente.paymentUrl && (
                <Button variant="btn_add" onClick={abrirPagamentoEfi}>
                  Tentar novamente na EFI
                </Button>
              )}
              <Button variant="btn_secondary" onClick={() => navigate(`/pagamento?plano=${planoId}`)}>
                Voltar ao checkout
              </Button>
            </div>
          </>
        )}

        {etapa === 'timeout' && (
          <>
            <Loader2 className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="text-xl font-bold text-gray-900">Ainda aguardando confirmação</h1>
            <p className="text-sm text-gray-600">
              Se você já pagou, aguarde alguns minutos ou clique abaixo para verificar de novo.
              Também confirmamos via webhook automaticamente.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                variant="btn_add"
                onClick={() => {
                  pollDesdeRef.current = Date.now()
                  setPollKey((k) => k + 1)
                  setEtapa('aguardando')
                }}
              >
                Verificar pagamento agora
              </Button>
              {pendente.paymentUrl && (
                <Button variant="btn_secondary" onClick={abrirPagamentoEfi}>
                  Abrir pagamento EFI
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
